import { CalculateServiceCostResult, Service, ServiceCostSummary, ServiceCostSummaryHomeCare } from '@anispi-reporting-system/types';
import { CheckCircleOutline } from '@mui/icons-material';
import { Box, Card, CardContent, Dialog, DialogTitle, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import { DownloadResult } from './DownloadResult';
import { ErrorMessages } from './ErrorMessages';
import { ResultHomeCare } from './ResultHomeCare';
import { ResultService } from './ResultService';
import { SnackbarMessageProps } from './SnackbarMessage';
import type { UploadFile } from '../hooks/useForm';
import { sendFile } from '../lib/Api';
import { labels } from '../lib/labels';

interface Props {
  companyName: string;
  companyNo: string;
  officeNo: string;
  onComplete: () => void;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<Omit<SnackbarMessageProps, 'onClose'>>>;
  service: Service;
  serviceCost: CalculateServiceCostResult['serviceCost'];
  uploadFileSe: UploadFile;
  uploadFileSv: UploadFile;
}

const SendResultDialog = ({
  companyName,
  companyNo,
  officeNo,
  setSnackbarMessage,
  service,
  serviceCost,
  uploadFileSe,
  uploadFileSv,
  open,
  setOpen,
  onComplete,
}: Props) => {
  const [confirm, setConfirm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  useEffect(() => {
    setConfirm(true);
  }, [companyNo, officeNo, service, serviceCost, uploadFileSe, uploadFileSv]);

  const handleClose = () => {
    setOpen(false);
    onComplete();
    localStorage.setItem('companyName', companyName);
    localStorage.setItem('companyNo', companyNo);
    localStorage.setItem('officeNo', officeNo);
  };

  const handleClick = async () => {
    setLoading(true);
    setErrorMessages([]);

    try {
      const result = await sendFile({
        companyName,
        companyNo,
        fileNameSe: uploadFileSe.uploadKey,
        fileNameSv: uploadFileSv.uploadKey,
        officeNo,
        service,
      });

      if (result.success === undefined || result.success === false) {
        if (result.errorMessages && result.errorMessages.length > 0) {
          setErrorMessages(result.errorMessages);
        } else {
          setErrorMessages(['エラーが発生しました。お手数ですが最初からもう一度お試しください']);
        }

        setSnackbarMessage({
          message: '送信に失敗しました。',
          open: true,
          severity: 'error',
        });

        return;
      }

      setConfirm(false);
    } catch {
      setSnackbarMessage({
        message: '送信に失敗しました。',
        open: true,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const title = confirm ? '送信確認' : '送信完了';

  return (
    <Dialog
      onClose={() => {
        if (loading) {
          return;
        }
        setOpen(false);
      }}
      open={open}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle className="text-center font-bold">{title}</DialogTitle>

      <Grid container padding={2} rowSpacing={2}>
        {confirm && (
          <Grid item xs={12}>
            <p className="font-bold text-red-500 underline lg:text-center">
              送信内容を確認のうえ、 「送信する」ボタンをクリックしてください。
            </p>
          </Grid>
        )}
        <Grid item xs={3}>
          企業名
        </Grid>
        <Grid item xs={9}>
          {companyName}
        </Grid>
        <Grid item xs={3}>
          企業No
        </Grid>
        <Grid item xs={9}>
          {companyNo}
        </Grid>
        <Grid item xs={3}>
          事業所番号
        </Grid>
        <Grid item xs={9}>
          {officeNo}
        </Grid>

        <Grid item xs={3}>
          サービス
        </Grid>
        <Grid item xs={9}>
          {labels.Service[service]}
        </Grid>

        <Grid item xs={3}>
          {service === 'HOMECARE-RY' || service === 'HOMECARE-RS' ? '【保険内】' : '【SE】'}
        </Grid>
        <Grid item xs={9}>
          {uploadFileSe.filename}
        </Grid>

        <Grid item xs={3}>
          {service === 'HOMECARE-RY' || service === 'HOMECARE-RS' ? '【保険外】' : '【SV】'}
        </Grid>
        <Grid item xs={9}>
          {uploadFileSv.filename}
        </Grid>

        {confirm ? (
          <>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box className="w-full overflow-y-scroll">
                    {(service === 'HOMECARE-RS' || service === 'HOMECARE-RY') && (
                      <ResultHomeCare service={service} serviceCostSummary={serviceCost as ServiceCostSummaryHomeCare} />
                    )}

                    {(service === 'RS' || service === 'RY' || service === 'RYSHORT') && (
                      <ResultService dialog service={service} serviceCostSummary={serviceCost as ServiceCostSummary} />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {errorMessages.length > 0 && (
              <Grid item xs={12} className="py-2">
                <ErrorMessages errorMessages={errorMessages} />
              </Grid>
            )}

            <Grid item xs={12} textAlign="center" padding={2}>
              <Button className="w-40" loading={loading} variant="contained" onClick={handleClick}>
                送信する
              </Button>
            </Grid>

            <Grid item xs={12} textAlign="center">
              <Button variant="outlined" disabled={loading} onClick={() => setOpen(false)}>
                閉じる
              </Button>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} marginTop={2} textAlign="center" height="60px">
              <CheckCircleOutline color="success" />
              ファイルを送信しました。
            </Grid>

            <Grid item xs={2} />
            <Grid item xs={4} textAlign="center">
              <DownloadResult
                companyName={companyName}
                companyNo={companyNo}
                officeNo={officeNo}
                service={service}
                serviceCost={serviceCost}
              />
            </Grid>
            <Grid item xs={4} textAlign="center">
              <Button variant="outlined" onClick={handleClose}>
                閉じる
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </Dialog>
  );
};

export { SendResultDialog };
